import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useUnreadCount from '../../hooks/useUnreadCount';
import { useTheme } from '../../contexts/ThemeContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { ONBOARDING_LOCK_MESSAGE } from '../../lib/onboarding';
import { Button } from '../ui/button';
import ConfidentialWatermark from '../ConfidentialWatermark';
import { toast } from 'sonner';
import { 
  Shield, 
  Home,
  Calendar, 
  CalendarCheck,
  Clock, 
  LogOut,
  Sun,
  Moon,
  HelpCircle,
  Menu,
  X,
  FileText,
  GraduationCap,
  CalendarRange,
  Lock,
  User,
  BookOpen,
  ClipboardList,
  Settings,
  Banknote,
  Package,
  ChevronDown,
  Store,
  ShieldCheck,
  FileSearch,
  ClipboardCheck,
  Briefcase,
  Archive,
  MessageSquare
, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import BackButton from '../BackButton';
import AnnouncementGate from '../AnnouncementGate';

// Routes that stay accessible while a new / reactivated employee is
// completing onboarding. Keep this list in sync with `allowWhileOnboarding`
// props on the corresponding <Route> elements in App.js.
const ONBOARDING_ALLOWED_PATHS = new Set([
  '/employee/my-profile',
  '/employee/legal',
  '/employee/terms',
  '/employee/help',
  '/employee/mandatory-training',
]);

// Visual accents for menu categories — SAN yellow stays the active colour.
const ACCENTS = {
  blue:    { text: 'text-blue-500 dark:text-blue-400',     border: 'border-blue-500/60',    bg: 'bg-blue-500/[0.06]' },
  green:   { text: 'text-green-600 dark:text-green-400',   border: 'border-green-500/60',   bg: 'bg-green-500/[0.06]' },
  purple:  { text: 'text-purple-500 dark:text-purple-400', border: 'border-purple-500/60',  bg: 'bg-purple-500/[0.06]' },
  slate:   { text: 'text-slate-500 dark:text-slate-400',   border: 'border-slate-500/60',   bg: 'bg-slate-500/[0.06]' },
  emerald: { text: 'text-emerald-500 dark:text-emerald-400', border: 'border-emerald-500/60', bg: 'bg-emerald-500/[0.06]' },
};

const navItems = [
  { icon: Home, label: 'Home', path: '/employee' },
  { icon: User, label: 'My Profile', path: '/employee/my-profile' },
  { icon: MessageSquare, label: 'Messages', path: '/employee/messages' },
  { icon: Briefcase, label: 'Work', accent: 'blue', children: [
    { icon: Calendar, label: 'My Shifts', path: '/employee/shifts' },
    { icon: CalendarCheck, label: 'Shifts Available', path: '/employee/available-shifts' },
    { icon: CalendarRange, label: 'Events List', path: '/employee/events-list' },
    { icon: Archive, label: 'Archived Events', path: '/employee/archived-events' },
    { icon: Clock, label: 'History', path: '/employee/history' },
  ] },
  { icon: Banknote, label: 'My Payslips', path: '/employee/payslips' },
  { icon: Package, label: 'My Kit', path: '/employee/kit' },
  { icon: ClipboardList, label: 'Reports', accent: 'green', children: [
    { icon: ClipboardList, label: 'Duty Reports', path: '/employee/duty-reports' },
  ] },
  { icon: FileText, label: 'My Documents & Compliance', accent: 'purple', children: [
    { icon: GraduationCap, label: 'Academic Breaks', path: '/employee/academic-breaks' },
    { icon: FileText, label: 'Legal & Policies', path: '/employee/legal' },
    { icon: BookOpen, label: 'Mandatory Training', path: '/employee/mandatory-training' },
  ] },
  { icon: Settings, label: 'Support & Settings', accent: 'slate', children: [
    { icon: Settings, label: 'Settings', path: '/employee/settings' },
    { icon: HelpCircle, label: 'FAQ', path: '/employee/faq' },
    { icon: HelpCircle, label: 'Help', path: '/employee/help' },
  ] },
];

const OPS_NAV_ITEMS = [
  { icon: CalendarRange, label: 'Event Calendar', path: '/employee/ops/events' },
  { icon: ClipboardList, label: 'Events Manager', path: '/employee/ops/events-manager' },
  { icon: Calendar, label: 'Retail Calendar', path: '/employee/ops/retail' },
  { icon: FileText, label: 'Staffing Requests', path: '/employee/ops/staffing' },
  { icon: MessageSquare, label: 'Messages', path: '/employee/ops/messages' },
  { icon: Clock, label: 'My Deletion Requests', path: '/employee/ops/deletion-requests' },
];

const CR_NAV_ITEMS = [
  { icon: CalendarRange, label: 'Event Calendar', path: '/employee/cr/events' },
  { icon: Calendar, label: 'Retail Calendar', path: '/employee/cr/retail' },
  { icon: FileText, label: 'Messages', path: '/employee/cr/messages' },
  { icon: Clock, label: 'Announcements', path: '/employee/cr/announcements' },
];

const PAYROLL_NAV_ITEMS = [
  { icon: Clock, label: 'Clock History', accent: 'emerald', children: [
    { icon: Store, label: 'Retail Clock History', path: '/employee/pay/clock-history/retail' },
    { icon: CalendarRange, label: 'Event Clock History', path: '/employee/pay/clock-history/event' },
  ]},
  { icon: Users, label: 'Employee Details', path: '/employee/pay/employees' },
  { icon: CalendarRange, label: 'Holiday Entitlement', path: '/employee/pay/holiday' },
  { icon: Clock, label: 'Breaks', path: '/employee/pay/breaks' },
  { icon: FileText, label: 'Payroll Reports', path: '/employee/pay/reports' },
  { icon: MessageSquare, label: 'Messages', path: '/employee/pay/messages' },
  { icon: Banknote, label: 'Payslips', path: '/employee/pay/payslips' },
];

const HR_NAV_ITEMS = [
  { icon: ShieldCheck, label: 'HR & Compliance', path: '/employee/hr/compliance' },
  { icon: ClipboardList, label: 'Compliance Queue', path: '/employee/hr/compliance-queue' },
  { icon: FileSearch, label: 'Document Review', path: '/employee/hr/document-review' },
  { icon: GraduationCap, label: 'Academic Breaks', path: '/employee/hr/academic-breaks' },
  { icon: FileText, label: 'Legal & Policies', path: '/employee/hr/legal' },
  { icon: ClipboardCheck, label: 'Policy Compliance', path: '/employee/hr/policy-compliance' },
  { icon: BookOpen, label: 'Mandatory Training', path: '/employee/hr/mandatory-training' },
  { icon: Users, label: 'Recruitment & Onboarding', accent: 'purple', children: [
    { icon: Briefcase, label: 'Job Applications', path: '/employee/hr/job-applications' },
    { icon: ClipboardCheck, label: 'Onboarding', path: '/employee/hr/onboarding' },
  ] },
  { icon: MessageSquare, label: 'Messages', path: '/employee/hr/messages' },
];

const DEPT_CONFIG = {
  'Operations':      { items: OPS_NAV_ITEMS,     color: 'text-blue-400',    prefix: 'ops' },
  'Control Room':    { items: CR_NAV_ITEMS,      color: 'text-orange-400',  prefix: 'cr' },
  'Payroll':         { items: PAYROLL_NAV_ITEMS, color: 'text-emerald-400', prefix: 'pay' },
  'HR & Compliance': { items: HR_NAV_ITEMS,      color: 'text-purple-400',  prefix: 'hr' },
};

export default function EmployeeLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const MSG_DEPTS = ['Control Room', 'Payroll', 'HR & Compliance', 'Operations'];
  const myUnread = useUnreadCount(`${API_BASE}/users/me/messages/unread-count`);
  const deptUnread = useUnreadCount(
    `${API_BASE}/admin/staff-messages/unread-count`,
    MSG_DEPTS.includes(user?.admin_department));
  const { theme, toggleTheme } = useTheme();
  const { locked } = useOnboarding();
  const [menuOpen, setMenuOpen] = useState(false);
  const [myAreaOpen, setMyAreaOpen] = useState(true);
  const [deptOpen, setDeptOpen] = useState(false);
  const [openSub, setOpenSub] = useState(() => {
    const g = navItems.find((i) => i.children?.some((c) => c.path === window.location.pathname));
    return g?.label || null;
  });

  const deptCfg = user?.role === 'employee' && !locked
    ? DEPT_CONFIG[user?.admin_department] || null
    : null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLockedClick = (e, label) => {
    e.preventDefault();
    setMenuOpen(false);
    toast.error(ONBOARDING_LOCK_MESSAGE, { duration: 6000 });
  };

  const renderItem = (item, prefix = '', nested = false) => {
    if (item.children) {
      const subOpen = openSub === item.label;
      const childActive = item.children.some((c) => location.pathname === c.path);
      const ac = ACCENTS[item.accent] || ACCENTS.slate;
      return (
        <div key={item.label} className={cn(subOpen && ac.bg)}>
          <button
            type="button"
            onClick={() => setOpenSub(subOpen ? null : item.label)}
            data-testid={`nav-${prefix ? prefix + '-' : ''}${item.label.toLowerCase().replace(/\s+/g, '-')}-sub`}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 transition-colors border-l-2',
              subOpen || childActive ? ac.border : 'border-transparent',
              childActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
            )}
          >
            <item.icon className={cn('w-5 h-5', ac.text)} strokeWidth={1.75} />
            <span className="text-sm uppercase tracking-wider font-bold flex-1 text-left">{item.label}</span>
            <ChevronDown
              className={cn('w-4 h-4 transition-transform duration-200', ac.text, subOpen && 'rotate-180')}
              strokeWidth={2}
            />
          </button>
          {subOpen && (
            <div className={cn('ml-6 border-l-2 pb-1', ac.border)}>
              {item.children.map((c) => renderItem(c, prefix, true))}
            </div>
          )}
        </div>
      );
    }
    const isActive = location.pathname === item.path;
    const isLocked = locked && !ONBOARDING_ALLOWED_PATHS.has(item.path);
    const tid = `nav-${prefix ? prefix + '-' : ''}${item.label.toLowerCase().replace(/\s+/g, '-')}`;
    if (isLocked) {
      return (
        <button
          key={item.path}
          type="button"
          data-testid={`nav-locked-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          onClick={(e) => handleLockedClick(e, item.label)}
          className="w-full flex items-center gap-3 px-4 py-3 text-muted-foreground/50 cursor-not-allowed"
          title={ONBOARDING_LOCK_MESSAGE}
        >
          <item.icon className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-wider font-medium flex-1 text-left">{item.label}</span>
          <Lock className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      );
    }
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={tid}
        onClick={() => setMenuOpen(false)}
        className={cn(
          "flex items-center gap-3 px-4 py-3 transition-colors",
          nested && "pl-5 py-2.5",
          isActive
            ? "text-primary bg-primary/10 border-l-2 border-primary -ml-[2px]"
            : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
        )}
      >
        <item.icon className={cn('flex-shrink-0', nested ? 'w-4 h-4 opacity-80' : 'w-5 h-5')} strokeWidth={1.5} />
        <span className={cn('uppercase tracking-wider', nested ? 'text-[13px] font-medium' : 'text-sm font-medium')}>{item.label}</span>
        {item.path === '/employee/messages' && myUnread > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="nav-messages-unread-badge">{myUnread}</span>
        )}
        {/\/(cr|pay|hr|ops)\/messages$/.test(item.path) && deptUnread > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="nav-dept-messages-unread-badge">{deptUnread}</span>
        )}
      </Link>
    );
  };

  const SectionHeader = ({ label, color, open, onToggle, testid }) => (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className="w-full flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30 hover:bg-accent/50 transition-colors"
    >
      <span className={cn('text-xs uppercase tracking-widest font-bold', color)}>{label}</span>
      <ChevronDown className={cn('w-4 h-4 transition-transform duration-200', color, open && 'rotate-180')} strokeWidth={2} />
    </button>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AnnouncementGate />
      <ConfidentialWatermark user={user} />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="menu-toggle-btn"
              className="text-muted-foreground hover:text-foreground -ml-2"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <span className="font-barlow text-lg tracking-wider text-primary uppercase">San Security</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              className="text-muted-foreground hover:text-foreground"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            {!deptCfg && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Slide-down menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}>
          <div
            className="absolute top-[57px] left-0 right-0 bg-card border-b border-border shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-lg mx-auto py-2 max-h-[calc(100vh-80px)] overflow-y-auto">
              {deptCfg ? (
                <>
                  {/* Accordion: MY AREA */}
                  <SectionHeader
                    label="My Area"
                    color="text-primary"
                    open={myAreaOpen}
                    onToggle={() => setMyAreaOpen((v) => !v)}
                    testid="section-my-area"
                  />
                  {myAreaOpen && navItems.map((item) => renderItem(item))}
                  {/* Accordion: DEPARTMENT */}
                  <SectionHeader
                    label={user.admin_department}
                    color={deptCfg.color}
                    open={deptOpen}
                    onToggle={() => setDeptOpen((v) => !v)}
                    testid={`section-${deptCfg.prefix}`}
                  />
                  {deptOpen && deptCfg.items.map((item) => renderItem(item, deptCfg.prefix))}
                  {/* Logout — always the very last item */}
                  <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="sidebar-logout-btn"
                    className="w-full flex items-center gap-3 px-4 py-3 border-t border-border text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                  >
                    <LogOut className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-sm uppercase tracking-wider font-medium">Logout</span>
                  </button>
                </>
              ) : (
                navItems.map((item) => renderItem(item))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 pt-16 overflow-y-auto">
        <div className="max-w-lg mx-auto px-4">
          <div className="pt-3 empty:hidden">
            <BackButton />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
